// ABOUTME: Daylight field scene — infinite green plane with FIFA centre markings
// ABOUTME: Procedural blue sky, sun-direction Lambert lighting, broadcast-cam framing

import * as THREE from 'three';

// World units. Pennants live near the origin; the plane extends far enough
// in every direction to reach the horizon under any reasonable camera.
export const PITCH_SIZE = 5000;
export const CENTRE_CIRCLE_RADIUS = 9.15;     // FIFA spec
export const CENTRE_SPOT_RADIUS = 0.18;
export const LINE_WIDTH = 0.18;               // ≈ FIFA 12cm, slightly thickened for visibility

// Sun position is computed for Zurich/Blick time so the visible flare,
// shader lighting, and pennant shadows all agree on one luminary.
export const SUN_LOCATION = Object.freeze({ latitudeDeg: 47.3769, longitudeDeg: 8.5417 });
export const MIN_VISUAL_SUN_ELEVATION_DEG = 12;
export const SKY_DOME_RADIUS = 3200;
export const INITIAL_CAMERA_FOV_DEG = 60;
export const INITIAL_CAMERA_DISTANCE = 50;
export const INITIAL_CAMERA_HEIGHT = 9;
export const INITIAL_CAMERA_SUN_AZIMUTH_OFFSET_DEG = 150;
export const INITIAL_CAMERA_MOBILE_SUN_AZIMUTH_OFFSET_DEG = 172;
export const INITIAL_CAMERA_POSITION = Object.freeze({ x: 0, y: INITIAL_CAMERA_HEIGHT, z: INITIAL_CAMERA_DISTANCE });
export const INITIAL_CAMERA_TARGET = Object.freeze({ x: 0, y: 0, z: 0 });
// Swiss alpine backdrop — flat-vector style, built from real low-poly geometry
// (see buildAlpineHorizon). Two continuous ridgeline "accordion wall" layers: a
// far range of light snowy giants and a nearer teal massif, encircling the pitch
// with a few dominant peaks and lots of negative space. No texture asset.
export const MOUNTAIN_RING_DISTANCE = 1500;   // far range radius (primary)
export const MOUNTAIN_MAX_HEIGHT = 370;       // tallest far-range peak (hero horn)
export const MOUNTAIN_LAYER_COUNT = 2;        // far + mid ranges
export const FIELD_FOG_COLOR = 0xc6e3f1;
export const FIELD_FOG_NEAR = 240;
export const FIELD_FOG_FAR = 1850;

// Pitch palette
const COLOUR_GRASS = 0x3eb842;                // brighter base mowed grass green
const COLOUR_MARKING = 0xeef2ee;              // off-white painted lines
const COLOUR_SUN = 0xfff5e0;                  // ~5500K direct sunlight
const COLOUR_SKY_AMBIENT = 0xb8d0e8;          // soft blue sky fill
const COLOUR_SKY_ZENITH = 0x5eace7;
const COLOUR_SKY_HORIZON = 0xc6e3f1;
const COLOUR_SKY_HAZE = 0xe3f0f5;
const COLOUR_CLOUD = 0xf7fbff;
// Alpine backdrop palette — bare rock (up-facing vs shadowed) + snow, lightening
// with distance for aerial perspective. Snow dominates the tops; rock shows on
// the steep faces and ridges.
const COLOUR_ROCK_FAR_LIGHT = 0x9aa7b4;       // hazy far rock, lit
const COLOUR_ROCK_FAR_DARK = 0x5e6b78;        // hazy far rock, shadow
const COLOUR_ROCK_MID_LIGHT = 0x7c8894;       // nearer rock, lit
const COLOUR_ROCK_MID_DARK = 0x434e59;        // nearer rock, shadow
const COLOUR_PEAK_SNOW = 0xf7fafc;            // snow

// Dusk + night palette (used by getCelestialState when the sun dips low)
const COLOUR_SUN_DUSK = 0xff8a3a;             // warm orange near the horizon
const COLOUR_MOON_LIGHT = 0xcfd8ff;           // strong cool moonlight
const COLOUR_NIGHT_ZENITH = 0x040917;         // near-black night zenith
const COLOUR_NIGHT_HORIZON = 0x0e1830;        // deep navy horizon
const COLOUR_NIGHT_HAZE = 0x182238;           // dim night haze
const COLOUR_NIGHT_AMBIENT = 0x172541;        // cool dark ambient fill

// Mowing stripe spacing (metres). Each stripe is one band; alternation at
// 2× this period.
const STRIPE_PERIOD = 5.0;

/**
 * Approximate NOAA solar position. Azimuth is compass-style: 0° north (+z),
 * 90° east (+x), 180° south (-z), 270° west (-x).
 */
export function computeSolarPosition(date = new Date(), location = SUN_LOCATION) {
    const d = date instanceof Date ? date : new Date(date);
    const safeDate = Number.isFinite(d.getTime()) ? d : new Date();
    const start = Date.UTC(safeDate.getUTCFullYear(), 0, 0);
    const dayOfYear = Math.floor((safeDate.getTime() - start) / 86_400_000);
    const minutesUtc = safeDate.getUTCHours() * 60
        + safeDate.getUTCMinutes()
        + safeDate.getUTCSeconds() / 60
        + safeDate.getUTCMilliseconds() / 60_000;

    const gamma = 2 * Math.PI / 365 * (dayOfYear - 1 + (minutesUtc / 60 - 12) / 24);
    const equationOfTime = 229.18 * (
        0.000075
        + 0.001868 * Math.cos(gamma)
        - 0.032077 * Math.sin(gamma)
        - 0.014615 * Math.cos(2 * gamma)
        - 0.040849 * Math.sin(2 * gamma)
    );
    const declination = 0.006918
        - 0.399912 * Math.cos(gamma)
        + 0.070257 * Math.sin(gamma)
        - 0.006758 * Math.cos(2 * gamma)
        + 0.000907 * Math.sin(2 * gamma)
        - 0.002697 * Math.cos(3 * gamma)
        + 0.00148 * Math.sin(3 * gamma);

    const longitudeDeg = Number.isFinite(location?.longitudeDeg)
        ? location.longitudeDeg
        : SUN_LOCATION.longitudeDeg;
    const latitudeDeg = Number.isFinite(location?.latitudeDeg)
        ? location.latitudeDeg
        : SUN_LOCATION.latitudeDeg;
    const trueSolarMinutes = ((minutesUtc + equationOfTime + longitudeDeg * 4) % 1440 + 1440) % 1440;
    const hourAngleDeg = trueSolarMinutes / 4 < 0
        ? trueSolarMinutes / 4 + 180
        : trueSolarMinutes / 4 - 180;

    const latitude = THREE.MathUtils.degToRad(latitudeDeg);
    const hourAngle = THREE.MathUtils.degToRad(hourAngleDeg);
    const cosZenith = Math.sin(latitude) * Math.sin(declination)
        + Math.cos(latitude) * Math.cos(declination) * Math.cos(hourAngle);
    const zenith = Math.acos(THREE.MathUtils.clamp(cosZenith, -1, 1));
    const elevationDeg = 90 - THREE.MathUtils.radToDeg(zenith);
    const azimuth = Math.atan2(
        Math.sin(hourAngle),
        Math.cos(hourAngle) * Math.sin(latitude) - Math.tan(declination) * Math.cos(latitude),
    );
    const azimuthDeg = (THREE.MathUtils.radToDeg(azimuth) + 180 + 360) % 360;

    return { elevationDeg, azimuthDeg };
}

export function getVisualSolarPosition(date = new Date(), location = SUN_LOCATION) {
    const actual = computeSolarPosition(date, location);
    return {
        ...actual,
        actualElevationDeg: actual.elevationDeg,
        elevationDeg: Math.max(actual.elevationDeg, MIN_VISUAL_SUN_ELEVATION_DEG),
        clampedToDaylight: actual.elevationDeg < MIN_VISUAL_SUN_ELEVATION_DEG,
    };
}

export function sunDirectionFromPosition(elevationDeg, azimuthDeg) {
    const phi = THREE.MathUtils.degToRad(90 - elevationDeg);
    const theta = THREE.MathUtils.degToRad(azimuthDeg);
    return new THREE.Vector3().setFromSphericalCoords(1, phi, theta).normalize();
}

/**
 * World direction the sun shines from (surface -> sun). Consumed by the pitch
 * shader, sky, visible flare, directional light, and pennant shader.
 */
export function getSunDirection(date = new Date(), location = SUN_LOCATION) {
    const sun = getVisualSolarPosition(date, location);
    return sunDirectionFromPosition(sun.elevationDeg, sun.azimuthDeg);
}

const DEFAULT_SOLAR_POSITION = getVisualSolarPosition(new Date(), SUN_LOCATION);
export const SUN_ELEVATION_DEG = DEFAULT_SOLAR_POSITION.elevationDeg;
export const SUN_AZIMUTH_DEG = DEFAULT_SOLAR_POSITION.azimuthDeg;

/**
 * Simplified lunar ephemeris — Meeus-style main-term approximation. Accurate
 * to roughly ±0.5° which is plenty for placing the moon disc in the sky and
 * driving the night-fill light direction. The moon traces its own arc through
 * the sky as the date advances, drifting ~12° per day relative to the sun.
 */
export function computeLunarPosition(date = new Date(), location = SUN_LOCATION) {
    const j2000 = Date.UTC(2000, 0, 1, 12, 0, 0);
    const d = (date.getTime() - j2000) / 86_400_000;
    const longitudeDeg = Number.isFinite(location?.longitudeDeg)
        ? location.longitudeDeg
        : SUN_LOCATION.longitudeDeg;
    const latitudeDeg = Number.isFinite(location?.latitudeDeg)
        ? location.latitudeDeg
        : SUN_LOCATION.latitudeDeg;

    const deg2rad = THREE.MathUtils.degToRad;

    // Moon mean elements (degrees).
    const Lmean = (218.316 + 13.176396 * d) % 360;          // ecliptic longitude
    const Manom = (134.963 + 13.064993 * d) % 360;          // mean anomaly
    const Farg  = (93.272 + 13.229350 * d) % 360;           // arg. of latitude

    // Leading periodic perturbations — enough for a visually correct arc.
    const lambda = Lmean + 6.289 * Math.sin(deg2rad(Manom));  // ecliptic longitude
    const beta   = 5.128 * Math.sin(deg2rad(Farg));           // ecliptic latitude

    // Ecliptic → equatorial (mean obliquity of the ecliptic).
    const epsilon = deg2rad(23.4397);
    const lambdaR = deg2rad(lambda);
    const betaR = deg2rad(beta);
    const ra = Math.atan2(
        Math.sin(lambdaR) * Math.cos(epsilon) - Math.tan(betaR) * Math.sin(epsilon),
        Math.cos(lambdaR),
    );
    const dec = Math.asin(
        Math.sin(betaR) * Math.cos(epsilon)
        + Math.cos(betaR) * Math.sin(epsilon) * Math.sin(lambdaR),
    );

    // Local sidereal time (degrees → radians), and hour angle.
    const lstDeg = (280.16 + 360.9856235 * d + longitudeDeg) % 360;
    const hourAngle = deg2rad(lstDeg) - ra;

    const lat = deg2rad(latitudeDeg);
    const elevation = Math.asin(
        Math.sin(lat) * Math.sin(dec)
        + Math.cos(lat) * Math.cos(dec) * Math.cos(hourAngle),
    );
    const azimuth = Math.atan2(
        Math.sin(hourAngle),
        Math.cos(hourAngle) * Math.sin(lat) - Math.tan(dec) * Math.cos(lat),
    );

    return {
        elevationDeg: THREE.MathUtils.radToDeg(elevation),
        azimuthDeg: (THREE.MathUtils.radToDeg(azimuth) + 180 + 360) % 360,
    };
}

/**
 * Moon direction in world space — the unit vector pointing from the surface
 * toward the moon, using the simplified ephemeris above.
 */
export function getMoonDirection(date = new Date(), location = SUN_LOCATION) {
    const moon = computeLunarPosition(date, location);
    return sunDirectionFromPosition(moon.elevationDeg, moon.azimuthDeg);
}

/**
 * Time-of-day lighting state. Returns the primary light (sun by day, moon by
 * night), a blended sky palette, and the raw sun/moon directions used by the
 * sky shader to render the discs.
 *
 * `dayFactor` is 0 deep at night and 1 in full daylight, with a soft transition
 * through dusk so colours move continuously rather than snapping.
 */
export function getCelestialState(date = new Date(), location = SUN_LOCATION) {
    const sun = computeSolarPosition(date, location);
    const moon = computeLunarPosition(date, location);
    const sunDirection = sunDirectionFromPosition(sun.elevationDeg, sun.azimuthDeg);
    // Visible moon: pinned well above the ridge at night for atmosphere, dropped
    // below the horizon by day so the disc only shows after dark. Keeps the real
    // azimuth so it still drifts night to night and sits over different peaks.
    // The visible moon uses its real ephemeris position, so it sits below the
    // horizon (hidden behind the mountains/ground) through the core of the night
    // and only rises as a low crescent before dawn — true to the actual sky. The
    // night-fill light direction (below) is clamped separately for usability.
    const moonDirection = sunDirectionFromPosition(moon.elevationDeg, moon.azimuthDeg);

    const dayFactor = THREE.MathUtils.smoothstep(sun.elevationDeg, -6, 6);
    const isDay = sun.elevationDeg > -2;
    // Dusk peaks when the sun sits within ±8° of the horizon and tapers to 0
    // a few degrees above or below.
    const duskFactor = Math.max(0, 1 - Math.abs(sun.elevationDeg) / 8);

    const sunDayColor = new THREE.Color(COLOUR_SUN);
    const sunDuskColor = new THREE.Color(COLOUR_SUN_DUSK);
    const moonColor = new THREE.Color(COLOUR_MOON_LIGHT);

    let primaryDirection;
    let primaryColor;
    let primaryIntensity;
    if (isDay) {
        primaryDirection = sunDirection;
        const warmth = THREE.MathUtils.smoothstep(sun.elevationDeg, 0, 12);
        primaryColor = sunDuskColor.clone().lerp(sunDayColor, warmth);
        primaryIntensity = THREE.MathUtils.lerp(0.45, 1.08, dayFactor);
    } else {
        // Light direction is clamped above the horizon so the moon always
        // shines *down* on the pitch, even when the real moon happens to be
        // below the horizon — the user wants a guaranteed strong fill at
        // night. The visible moon disc (moonDirection) still uses the real
        // position and naturally disappears below the horizon.
        const moonLightElevation = Math.max(moon.elevationDeg, 18);
        primaryDirection = sunDirectionFromPosition(moonLightElevation, moon.azimuthDeg);
        primaryColor = moonColor.clone();
        primaryIntensity = 0.78;
    }

    const dayZenith = new THREE.Color(COLOUR_SKY_ZENITH);
    const dayHorizon = new THREE.Color(COLOUR_SKY_HORIZON);
    const dayHaze = new THREE.Color(COLOUR_SKY_HAZE);
    const nightZenith = new THREE.Color(COLOUR_NIGHT_ZENITH);
    const nightHorizon = new THREE.Color(COLOUR_NIGHT_HORIZON);
    const nightHaze = new THREE.Color(COLOUR_NIGHT_HAZE);

    const skyZenith = nightZenith.clone().lerp(dayZenith, dayFactor);
    const skyHorizon = nightHorizon.clone().lerp(dayHorizon, dayFactor);
    const skyHaze = nightHaze.clone().lerp(dayHaze, dayFactor);

    if (duskFactor > 0.05) {
        // Warm sunset/sunrise wash on the horizon and haze bands.
        const duskWash = new THREE.Color(0xff7a3a);
        skyHorizon.lerp(duskWash, duskFactor * 0.55);
        skyHaze.lerp(duskWash, duskFactor * 0.28);
    }

    const ambientColor = new THREE.Color(COLOUR_NIGHT_AMBIENT)
        .lerp(new THREE.Color(COLOUR_SKY_AMBIENT), dayFactor);
    const ambientIntensity = THREE.MathUtils.lerp(0.46, 0.68, dayFactor);
    const cloudOpacity = THREE.MathUtils.lerp(0.05, 0.26, dayFactor);

    return {
        sunDirection,
        moonDirection,
        primaryDirection,
        primaryColor,
        primaryIntensity,
        skyZenith,
        skyHorizon,
        skyHaze,
        ambientColor,
        ambientIntensity,
        cloudOpacity,
        dayFactor,
        isDay,
        sunElevationDeg: sun.elevationDeg,
    };
}

export function getInitialSunAzimuthOffsetDeg(aspect = 1512 / 796) {
    const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1512 / 796;
    const mobileBlend = THREE.MathUtils.clamp((0.8 - safeAspect) / 0.35, 0, 1);
    return THREE.MathUtils.lerp(
        INITIAL_CAMERA_SUN_AZIMUTH_OFFSET_DEG,
        INITIAL_CAMERA_MOBILE_SUN_AZIMUTH_OFFSET_DEG,
        mobileBlend,
    );
}

export function getInitialCameraPose(sunDirection = getSunDirection(), options = {}) {
    const {
        distance = INITIAL_CAMERA_DISTANCE,
        height = INITIAL_CAMERA_HEIGHT,
        aspect = 1512 / 796,
    } = options;
    const sunAzimuthOffsetDeg = options.sunAzimuthOffsetDeg ?? getInitialSunAzimuthOffsetDeg(aspect);
    const sun = normalizedSunDirection(sunDirection);
    const sunHorizontal = new THREE.Vector3(sun.x, 0, sun.z);
    if (sunHorizontal.lengthSq() < 1e-6) {
        sunHorizontal.set(0, 0, -1);
    } else {
        sunHorizontal.normalize();
    }

    const forward = sunHorizontal.applyAxisAngle(
        new THREE.Vector3(0, 1, 0),
        THREE.MathUtils.degToRad(sunAzimuthOffsetDeg),
    );

    const position = new THREE.Vector3(
        -forward.x * distance,
        height,
        -forward.z * distance,
    );
    const target = new THREE.Vector3(
        INITIAL_CAMERA_TARGET.x,
        INITIAL_CAMERA_TARGET.y,
        INITIAL_CAMERA_TARGET.z,
    );

    return { position, target };
}

function getInitialViewForward(sunDirection = getSunDirection(), aspect = 1512 / 796) {
    const pose = getInitialCameraPose(sunDirection, { aspect });
    const forward = pose.target.clone().sub(pose.position);
    forward.y = 0;
    if (forward.lengthSq() < 1e-6) return new THREE.Vector3(0, 0, -1);
    return forward.normalize();
}

export function getInitialCameraElevationDeg() {
    return THREE.MathUtils.radToDeg(Math.atan2(INITIAL_CAMERA_HEIGHT, INITIAL_CAMERA_DISTANCE));
}

// ─── Pitch plane ────────────────────────────────────────────────────────

const PITCH_VERT = /* glsl */ `
    varying vec3 vWorldPos;
    void main() {
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vWorldPos = worldPos.xyz;
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

const PITCH_FRAG = /* glsl */ `
    uniform vec3 uGrassColor;
    uniform vec3 uMarkingColor;
    uniform vec3 uSunDirection;
    uniform vec3 uSunColor;
    uniform vec3 uSkyAmbient;
    uniform float uSunIntensity;
    uniform float uSkyIntensity;
    uniform float uLineWidth;
    uniform float uCircleRadius;
    uniform float uSpotRadius;
    uniform float uStripePeriod;

    varying vec3 vWorldPos;

    float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
    float valueNoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(
            mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
            u.y
        );
    }
    float fbm(vec2 p) {
        float v = 0.0;
        float a = 0.5;
        for (int i = 0; i < 4; i++) {
            v += valueNoise(p) * a;
            p *= 2.0;
            a *= 0.5;
        }
        return v;
    }

    // Anti-aliased line: returns 1 inside the line, fading at the edges.
    float lineMask(float coord, float halfWidth) {
        float d = abs(coord);
        float aa = fwidth(coord) * 1.5;
        return 1.0 - smoothstep(halfWidth - aa, halfWidth + aa, d);
    }

    // Anti-aliased ring: returns 1 on the circle's outline of the given radius.
    float ringMask(vec2 p, float radius, float halfWidth) {
        float d = abs(length(p) - radius);
        float aa = fwidth(d) * 1.5;
        return 1.0 - smoothstep(halfWidth - aa, halfWidth + aa, d);
    }

    // Anti-aliased filled disc.
    float discMask(vec2 p, float radius) {
        float d = length(p);
        float aa = fwidth(d) * 1.5;
        return 1.0 - smoothstep(radius - aa, radius + aa, d);
    }

    void main() {
        vec2 p = vWorldPos.xz;        // p.x = world x, p.y = world z

        // Base grass: green plus FBM noise variation
        float grain = (fbm(p * 0.4) - 0.5) * 0.06;
        float coarse = (fbm(p * 0.05) - 0.5) * 0.08;
        vec3 base = uGrassColor + vec3(coarse * 0.4, grain + coarse, coarse * 0.4);

        // Soft mowing stripes near the planting area. At the flatter camera
        // angle, hard infinite bands read as texture tiling, so the stripe
        // contrast fades out into the distance and gets slight organic drift.
        float stripeDrift = (fbm(p * 0.025 + vec2(7.3, -2.1)) - 0.5) * 2.2;
        float stripeWave = sin((p.y + stripeDrift) * (3.14159265 / uStripePeriod));
        float stripeFade = 1.0 - smoothstep(70.0, 620.0, abs(p.y));
        float stripeBreakup = mix(0.82, 1.0, fbm(p * 0.055 + vec2(-1.8, 4.6)));
        base *= 1.0 + stripeWave * 0.045 * stripeFade * stripeBreakup;

        // Halfway line: thin line at z=0 extending to ±∞ along x
        float halfwayLine = lineMask(p.y, uLineWidth);

        // Centre circle: ring of radius CENTRE_CIRCLE_RADIUS at origin
        float centreCircle = ringMask(p, uCircleRadius, uLineWidth);

        // Centre spot: solid filled disc
        float centreSpot = discMask(p, uSpotRadius);

        float markings = max(max(halfwayLine, centreCircle), centreSpot);
        base = mix(base, uMarkingColor, markings);

        // Sun-direction Lambert + sky-dome ambient. The plane is flat so
        // N is constant — the lighting only affects intensity, but it
        // matches the pennant shader so they read as one scene.
        vec3 N = vec3(0.0, 1.0, 0.0);
        float sunDiff = max(dot(N, uSunDirection), 0.0);
        vec3 lighting = uSunColor * sunDiff * uSunIntensity
                      + uSkyAmbient * uSkyIntensity;

        vec3 color = base * lighting;

        // Atmospheric haze — fade towards the sky colour as the pitch
        // recedes to the horizon. Onset pushed back so the visible mid-ground
        // reads green instead of washing out into the sky tint.
        float dist = length(p);
        float fog = smoothstep(500.0, 1800.0, dist);
        color = mix(color, uSkyAmbient * 0.85, fog);

        gl_FragColor = vec4(color, 1.0);
    }
`;

function normalizedSunDirection(sunDirection) {
    return sunDirection
        ? new THREE.Vector3(sunDirection.x, sunDirection.y, sunDirection.z).normalize()
        : getSunDirection();
}

function buildPitchMesh(sunDirection = getSunDirection()) {
    const sun = normalizedSunDirection(sunDirection);
    const geometry = new THREE.PlaneGeometry(PITCH_SIZE, PITCH_SIZE);
    geometry.rotateX(-Math.PI / 2);   // lay flat so x/z is the surface

    const material = new THREE.ShaderMaterial({
        uniforms: {
            uGrassColor:     { value: new THREE.Color(COLOUR_GRASS) },
            uMarkingColor:   { value: new THREE.Color(COLOUR_MARKING) },
            uSunDirection:   { value: sun },
            uSunColor:       { value: new THREE.Color(COLOUR_SUN) },
            uSkyAmbient:     { value: new THREE.Color(COLOUR_SKY_AMBIENT) },
            uSunIntensity:   { value: 1.08 },
            uSkyIntensity:   { value: 0.68 },
            uLineWidth:      { value: LINE_WIDTH * 0.5 },     // shader uses half-width
            uCircleRadius:   { value: CENTRE_CIRCLE_RADIUS },
            uSpotRadius:     { value: CENTRE_SPOT_RADIUS },
            uStripePeriod:   { value: STRIPE_PERIOD },
        },
        vertexShader: PITCH_VERT,
        fragmentShader: PITCH_FRAG,
        side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'pitch';
    mesh.frustumCulled = false;
    return mesh;
}

// ─── Daylight procedural sky ────────────────────────────────────────────

const SKY_VERT = /* glsl */ `
    varying vec3 vWorldDirection;

    void main() {
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vWorldDirection = normalize(worldPos.xyz);
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

const SKY_FRAG = /* glsl */ `
    uniform vec3 uZenithColor;
    uniform vec3 uHorizonColor;
    uniform vec3 uHazeColor;
    uniform vec3 uSunDirection;
    uniform vec3 uMoonDirection;
    uniform vec3 uSunColor;
    uniform vec3 uCloudColor;
    uniform float uCloudOpacity;
    uniform float uNightFactor;

    varying vec3 vWorldDirection;

    float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
    float hash3(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453123); }
    float valueNoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(
            mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
            u.y
        );
    }
    float fbm(vec2 p) {
        float v = 0.0;
        float a = 0.5;
        for (int i = 0; i < 4; i++) {
            v += valueNoise(p) * a;
            p *= 2.0;
            a *= 0.5;
        }
        return v;
    }

    void main() {
        vec3 dir = normalize(vWorldDirection);
        float height = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);
        float skyMix = smoothstep(0.12, 0.92, height);
        vec3 color = mix(uHorizonColor, uZenithColor, skyMix);

        float horizonHaze = 1.0 - smoothstep(0.05, 0.42, height);
        color = mix(color, uHazeColor, horizonHaze * 0.28);

        vec2 cloudUv = dir.xz * vec2(2.8, 1.15) + vec2(height * 0.55, height * 2.4);
        float cloudNoise = fbm(cloudUv + vec2(3.7, 0.0));
        float cloudDetail = fbm(cloudUv * vec2(2.0, 1.25) + vec2(-4.0, 5.2));
        float cloudBand = smoothstep(0.20, 0.36, height) * (1.0 - smoothstep(0.72, 0.92, height));
        float cloudMask = smoothstep(0.62, 0.82, cloudNoise + cloudDetail * 0.32) * cloudBand;
        color = mix(color, uCloudColor, cloudMask * uCloudOpacity);

        // Stars — sparse points in the upper sky, fading in above the horizon and
        // only after dark. Drawn before the sun/moon so the discs sit over them.
        if (uNightFactor > 0.001) {
            vec3 sg = dir * 300.0;
            vec3 sc = floor(sg);
            float sh = hash3(sc);
            if (sh > 0.968) {
                vec3 sp = vec3(fract(sh * 57.0), fract(sh * 131.0), fract(sh * 197.0));
                float dstar = length(fract(sg) - sp);
                float star = smoothstep(0.11, 0.0, dstar) * (0.55 + 0.6 * fract(sh * 13.0));
                float aboveHorizon = smoothstep(0.02, 0.16, dir.y);
                color += vec3(0.90, 0.93, 1.0) * star * aboveHorizon * uNightFactor;
            }
        }

        // Sun disc + halo, only meaningful while the sun is above the horizon.
        float sunAbove = smoothstep(-0.04, 0.04, uSunDirection.y);
        float sunDot = max(dot(dir, normalize(uSunDirection)), 0.0);
        float sunDisc = smoothstep(0.9976, 0.9992, sunDot) * sunAbove;
        float innerGlow = pow(sunDot, 64.0) * 0.55 * sunAbove;
        float outerGlow = pow(sunDot, 8.0) * 0.16 * sunAbove;
        color = mix(color, vec3(1.0, 0.84, 0.34), sunDisc * 0.84);
        color += uSunColor * (sunDisc * 0.48 + innerGlow + outerGlow);

        // Moon disc + soft halo. Visible only when the moon is above the
        // horizon (i.e. the sun is below it).
        // A crescent moon (a nod to the real ~July-11 waning phase) with a soft
        // halo — the night's gentle light source.
        float moonAbove = smoothstep(-0.04, 0.04, uMoonDirection.y);
        vec3 mdir = normalize(uMoonDirection);
        float moonDot = max(dot(dir, mdir), 0.0);
        // Carve a crescent: local disc coords, then subtract an offset shadow disc.
        vec3 mRight = normalize(cross(vec3(0.0, 1.0, 0.0), mdir));
        vec3 mUp = cross(mdir, mRight);
        vec2 mLocal = vec2(dot(dir, mRight), dot(dir, mUp)) / 0.030;
        float mc = cos(0.6), ms = sin(0.6);            // tilt the horns a touch
        vec2 mTilt = vec2(mLocal.x * mc - mLocal.y * ms, mLocal.x * ms + mLocal.y * mc);
        float lit = smoothstep(0.94, 1.06, distance(mTilt, vec2(0.34, 0.0)));
        float moonDisc = smoothstep(0.99934, 0.99966, moonDot) * moonAbove * lit;
        float moonInnerGlow = pow(moonDot, 700.0) * 0.30 * moonAbove;
        float moonOuterGlow = pow(moonDot, 60.0) * 0.07 * moonAbove;
        color = mix(color, vec3(0.97, 0.98, 1.0), moonDisc);
        color += vec3(0.74, 0.80, 1.0) * (moonInnerGlow + moonOuterGlow);

        gl_FragColor = vec4(color, 1.0);
    }
`;

function buildDaylightSky(sunDirection = getSunDirection()) {
    const sun = normalizedSunDirection(sunDirection);
    const moon = sun.clone().multiplyScalar(-1);
    const geometry = new THREE.SphereGeometry(SKY_DOME_RADIUS, 32, 16);
    const material = new THREE.ShaderMaterial({
        uniforms: {
            uZenithColor:   { value: new THREE.Color(COLOUR_SKY_ZENITH) },
            uHorizonColor:  { value: new THREE.Color(COLOUR_SKY_HORIZON) },
            uHazeColor:     { value: new THREE.Color(COLOUR_SKY_HAZE) },
            uSunDirection:  { value: sun },
            uMoonDirection: { value: moon },
            uSunColor:      { value: new THREE.Color(COLOUR_SUN) },
            uCloudColor:    { value: new THREE.Color(COLOUR_CLOUD) },
            uCloudOpacity:  { value: 0.26 },
            uNightFactor:   { value: 0 },
        },
        vertexShader: SKY_VERT,
        fragmentShader: SKY_FRAG,
        side: THREE.BackSide,
        depthWrite: false,
    });
    const sky = new THREE.Mesh(geometry, material);
    sky.name = 'procedural-sky';
    sky.frustumCulled = false;
    sky.renderOrder = -10;
    return sky;
}

const SUN_FLARE_VERT = /* glsl */ `
    varying vec2 vUv;

    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const SUN_FLARE_FRAG = /* glsl */ `
    varying vec2 vUv;

    void main() {
        vec2 p = vUv * 2.0 - 1.0;
        float r = length(p);
        float core = 1.0 - smoothstep(0.00, 0.10, r);
        float innerHalo = 1.0 - smoothstep(0.08, 0.40, r);
        float outerHalo = 1.0 - smoothstep(0.16, 1.00, r);
        float rayX = (1.0 - smoothstep(0.00, 0.052, abs(p.y)))
                   * (1.0 - smoothstep(0.10, 1.00, abs(p.x)));
        float rayY = (1.0 - smoothstep(0.00, 0.038, abs(p.x)))
                   * (1.0 - smoothstep(0.12, 0.72, abs(p.y)));
        float alpha = clamp(core + innerHalo * 0.78 + outerHalo * 0.38 + rayX * 0.42 + rayY * 0.22, 0.0, 1.0);
        if (alpha < 0.01) discard;

        vec3 halo = vec3(1.0, 0.64, 0.18);
        vec3 hot = vec3(1.0, 0.97, 0.72);
        vec3 color = mix(halo, hot, clamp(core + innerHalo * 0.56, 0.0, 1.0));
        gl_FragColor = vec4(color, alpha);
    }
`;

function buildSunFlareMesh(sunDirection) {
    const sun = normalizedSunDirection(sunDirection);
    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.ShaderMaterial({
        vertexShader: SUN_FLARE_VERT,
        fragmentShader: SUN_FLARE_FRAG,
        transparent: true,
        depthWrite: false,
        // depthTest ON so the mountain ridge occludes the flare — the sun rises
        // and sets *behind* the peaks instead of floating in front of them. The
        // flare sits beyond the mountain ring (below), so the opaque range hides
        // whatever dips behind it.
        depthTest: true,
        blending: THREE.NormalBlending,
        toneMapped: false,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'sun-flare';
    mesh.position.copy(sun).multiplyScalar(SKY_DOME_RADIUS * 0.74);
    mesh.scale.setScalar(760);
    mesh.renderOrder = 100;
    mesh.frustumCulled = false;
    mesh.userData.sunDirection = sun.clone();
    mesh.onBeforeRender = (_renderer, _scene, camera) => {
        mesh.quaternion.copy(camera.quaternion);
    };
    return mesh;
}

function buildSunLightRig(sunDirection = getSunDirection()) {
    const sun = normalizedSunDirection(sunDirection);
    const rig = new THREE.Group();
    rig.name = 'sun-light-rig';
    rig.userData.sunDirection = sun.clone();

    const light = new THREE.DirectionalLight(COLOUR_SUN, 2.2);
    light.name = 'sun-directional-light';
    light.position.copy(sun).multiplyScalar(280);
    light.target.position.set(0, 0, 0);

    rig.add(light);
    rig.add(light.target);
    rig.add(buildSunFlareMesh(sun));
    return rig;
}

// Flat-vector alpine massif — the "accordion wall". Instead of separate tents,
// each layer is ONE continuous ridgeline around the pitch: an alternating run of
// peaks and saddles, so subordinate summits fuse into a coherent carved wall
// with a few dominant snowy peaks and lots of negative space (real ranges read
// this way, not as a uniform sawtooth). Peaks are pushed inward (a radial fold)
// so the two adjacent faces angle apart → crisp low-poly two-tone. Snow is keyed
// to an absolute world height so only the tallest peaks are capped. The range
// dims to a dark silhouette at night via uDaylight.
const RIDGE_VERT = /* glsl */ `
    varying vec3 vWorldNormal;
    varying vec3 vWorldPos;
    varying float vWorldY;

    void main() {
        // Flat per-face normals (from computeVertexNormals on non-indexed geo),
        // in world space so the sun lights the range consistently.
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldNormal = normalize(mat3(modelMatrix) * normal);
        vWorldPos = wp.xyz;
        vWorldY = wp.y;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const RIDGE_FRAG = /* glsl */ `
    uniform vec3 uSunDir;
    uniform vec3 uSnowColor;
    uniform vec3 uRockLight;
    uniform vec3 uRockDark;
    uniform float uSnowLineY;   // world height where snow starts
    uniform float uSnowBand;    // softness of the snow line
    uniform float uDaylight;
    uniform vec3 uNightColor;
    uniform vec3 uHazeColor;    // atmospheric haze / sky tint the range fades into
    uniform float uHazeStrength;
    uniform vec3 uCloudColor;   // low-cloud band wrapping the mountains' waist
    uniform float uCloudBandY;
    uniform float uCloudBandWidth;
    uniform float uCloudBandStrength;

    varying vec3 vWorldNormal;
    varying vec3 vWorldPos;
    varying float vWorldY;

    // Value noise on world position — breaks the flat grey into rock patches.
    float h3(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
    float vnoise(vec3 p) {
        vec3 i = floor(p); vec3 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(
            mix(mix(h3(i), h3(i + vec3(1,0,0)), f.x), mix(h3(i + vec3(0,1,0)), h3(i + vec3(1,1,0)), f.x), f.y),
            mix(mix(h3(i + vec3(0,0,1)), h3(i + vec3(1,0,1)), f.x), mix(h3(i + vec3(0,1,1)), h3(i + vec3(1,1,1)), f.x), f.y),
            f.z);
    }

    void main() {
        vec3 N = normalize(vWorldNormal);
        if (!gl_FrontFacing) N = -N;
        float up = clamp(N.y, 0.0, 1.0);      // 1 = faces sky, 0 = vertical cliff
        float steep = 1.0 - up;

        // Snow blankets high ground, but the steepest cliff faces stay bare rock —
        // that snow-on-shoulders / rock-on-faces read is what says "alps".
        float elev = smoothstep(uSnowLineY - uSnowBand, uSnowLineY + uSnowBand, vWorldY);
        // Snow blankets most of the peak; only the steepest cliff faces shed to
        // bare rock, so the range reads snow-dominant like the real alps.
        float snow = elev * (1.0 - smoothstep(0.6, 0.92, steep));
        vec3 rock = mix(uRockDark, uRockLight, up);

        // Break the uniform grey: mottle the rock with warm-ochre and cool-slate
        // patches (two noise octaves), tint warmer low / cooler high, and add faint
        // horizontal strata — so the faces read as weathered stone, not flat card.
        float nBig = vnoise(vWorldPos * 0.011);
        float nFine = vnoise(vWorldPos * 0.05 + 21.0);
        float mott = clamp(nBig * 0.7 + nFine * 0.3, 0.0, 1.0);
        vec3 warmRock = rock * vec3(1.16, 1.03, 0.82);   // ochre / iron patches
        vec3 coolRock = rock * vec3(0.85, 0.91, 1.08);   // slate / shadow patches
        rock = mix(rock, warmRock, smoothstep(0.42, 0.85, mott));
        rock = mix(rock, coolRock, smoothstep(0.42, 0.85, 1.0 - mott));
        float elevT = clamp(vWorldY / max(uSnowLineY, 1.0), 0.0, 1.0);
        rock *= mix(vec3(1.07, 0.99, 0.87), vec3(0.93, 0.96, 1.05), elevT);
        rock *= 0.95 + 0.05 * sin(vWorldY * 0.22 + nFine * 6.28);

        vec3 albedo = mix(rock, uSnowColor, snow);

        // Directional sun for depth and drama (shadowed snow stays fairly bright).
        float diff = max(dot(N, normalize(uSunDir)), 0.0);
        vec3 color = albedo * (0.46 + 0.7 * diff);

        vec3 nightColor = mix(color * 0.13, uNightColor, 0.6);
        color = mix(nightColor, color, uDaylight);

        // Atmospheric haze — distant summits and the low bases fade into the sky,
        // the aerial-perspective cue that keeps the range from looking cut-out.
        float dist = distance(cameraPosition, vWorldPos);
        float distHaze = smoothstep(500.0, 2600.0, dist);
        float baseHaze = 1.0 - smoothstep(0.0, 230.0, vWorldY);
        float haze = clamp(distHaze * 0.75 + baseHaze * 0.55, 0.0, 1.0) * uHazeStrength;
        color = mix(color, uHazeColor, haze);

        // A soft band of low cloud wrapping the mountains' waist — the peaks rise
        // out of it, the way morning mist sits in the alpine valleys. It follows
        // the day/night cycle so it stays dark at night instead of glowing.
        float band = exp(-pow((vWorldY - uCloudBandY) / uCloudBandWidth, 2.0));
        vec3 cloudNight = mix(uCloudColor * 0.12, uNightColor, 0.6);
        vec3 cloudCol = mix(cloudNight, uCloudColor, uDaylight);
        color = mix(color, cloudCol, band * uCloudBandStrength);

        gl_FragColor = vec4(color, 1.0);
    }
`;

// Deterministic 0..1 hash so the range is stable across reloads.
function tentHash01(x) {
    const s = Math.sin(x) * 43758.5453;
    return s - Math.floor(s);
}

/**
 * Build one range layer as a run of overlapping peaks. Each peak is a sloped
 * cone with an ELLIPTICAL base — wide along the ring (so neighbours overlap into
 * a connected massif) but narrow radially (so the profile stays a sharp summit).
 * A power law gives a few tall peaks + many low ones, plus one dominant horn.
 * The sloped faces (unlike a vertical curtain) catch the sun and hold snow, so
 * the lit snow/rock shader reads as real alpine terrain.
 */
function buildAlpineRange({
    name,
    radius,
    peaks,
    minHeight,
    maxHeight,
    radialSlope,        // radial base half-width as a fraction of height (sharpness)
    tangentOverlap,     // tangential half-width as a fraction of peak spacing (>0.5 overlaps)
    sides = 7,
    jitter = 0.7,
    snowLineY,
    snowBand,
    heroAngle,
    heroBoost = 0,
    heroWidth = 0.12,
    heroRadialSlope = 0.36,
    rockLight,
    rockDark,
    snowColor = COLOUR_PEAK_SNOW,
    hazeStrength = 0.7,
    cloudBandY = 162,
    cloudBandWidth = 66,
    cloudBandStrength = 0.92,
    sunDir,
    seedBase = 1.0,
    renderOrder = 2,
}) {
    const TWO_PI = Math.PI * 2;
    const floorFrac = minHeight / maxHeight;
    const spacing = (TWO_PI / peaks) * radius;
    const positions = [];
    const pushTri = (a, b, c) => { for (const v of [a, b, c]) positions.push(v[0], v[1], v[2]); };

    for (let i = 0; i < peaks; i++) {
        const ja = (tentHash01(i * 3.1 + seedBase) - 0.5) * (TWO_PI / peaks) * jitter;
        const angle = (i / peaks) * TWO_PI + ja;
        let d = angle - heroAngle;
        d = Math.atan2(Math.sin(d), Math.cos(d));
        const hero = Math.exp(-(d * d) / (2 * heroWidth * heroWidth)) * heroBoost;
        const isHero = hero > 0.25;
        const hn = Math.min(1, floorFrac + (1 - floorFrac) * Math.pow(tentHash01(i * 7.7 + seedBase), 2.2) + hero);
        const h = maxHeight * hn;
        const bwR = h * (isHero ? heroRadialSlope : radialSlope);       // radial (sharp)
        const bwT = spacing * tangentOverlap;                          // tangential (overlap)

        const cx = Math.cos(angle) * radius;
        const cz = Math.sin(angle) * radius;
        const radialDir = [Math.cos(angle), Math.sin(angle)];
        const tangentDir = [-Math.sin(angle), Math.cos(angle)];
        // Asymmetric apex (offset along the ring) — real horns aren't isosceles.
        const off = (tentHash01(i * 2.3 + seedBase) - 0.5) * bwT * (isHero ? 0.5 : 0.28);
        const apex = [cx + tangentDir[0] * off, h, cz + tangentDir[1] * off];
        const phase = tentHash01(i * 4.9 + seedBase) * TWO_PI;

        // Faceted low-poly peak: stacked rings from base to apex, each vertex
        // jittered in angle / radius / height so the summit reads as a rugged
        // rock-and-snow mass of many small facets rather than a smooth cone.
        const RINGS = 5; // more vertical facets → richer, more complex peaks
        const jag = isHero ? 0.22 : 0.42; // keep the hero horn a cleaner spire
        const grid = [];
        for (let r = 0; r < RINGS; r++) {
            const t = r / RINGS;
            const row = [];
            for (let k = 0; k < sides; k++) {
                const nA = tentHash01(i * 17.3 + r * 4.1 + k * 2.7 + seedBase);
                const nR = tentHash01(i * 31.1 + r * 5.7 + k * 3.3 + seedBase);
                const nY = tentHash01(i * 11.9 + r * 6.3 + k * 1.9 + seedBase);
                const ang = phase + (k / sides) * TWO_PI + (nA - 0.5) * (TWO_PI / sides) * 0.6 * jag;
                const rR = bwR * (1 - t) * (1 + (nR - 0.5) * jag);
                const rT = bwT * (1 - t) * (1 + (nA - 0.5) * jag);
                const ct = Math.cos(ang) * rT;
                const sr = Math.sin(ang) * rR;
                const yv = Math.max(0, h * t + (nY - 0.5) * h * 0.06);
                row.push([cx + tangentDir[0] * ct + radialDir[0] * sr, yv, cz + tangentDir[1] * ct + radialDir[1] * sr]);
            }
            grid.push(row);
        }
        for (let r = 0; r < RINGS - 1; r++) {
            for (let k = 0; k < sides; k++) {
                const k1 = (k + 1) % sides;
                pushTri(grid[r][k], grid[r + 1][k], grid[r + 1][k1]);
                pushTri(grid[r][k], grid[r + 1][k1], grid[r][k1]);
            }
        }
        for (let k = 0; k < sides; k++) {
            pushTri(grid[RINGS - 1][k], apex, grid[RINGS - 1][(k + 1) % sides]);
        }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.computeVertexNormals(); // non-indexed → flat per-face normals

    const material = new THREE.ShaderMaterial({
        vertexShader: RIDGE_VERT,
        fragmentShader: RIDGE_FRAG,
        uniforms: {
            uSunDir: { value: sunDir.clone() },
            uSnowColor: { value: new THREE.Color(snowColor) },
            uRockLight: { value: new THREE.Color(rockLight) },
            uRockDark: { value: new THREE.Color(rockDark) },
            uSnowLineY: { value: snowLineY },
            uSnowBand: { value: snowBand },
            uDaylight: { value: 1 },
            uNightColor: { value: new THREE.Color(0x0d1622) },
            uHazeColor: { value: new THREE.Color(COLOUR_SKY_HAZE) },
            uHazeStrength: { value: hazeStrength },
            uCloudColor: { value: new THREE.Color(COLOUR_CLOUD) },
            uCloudBandY: { value: cloudBandY },
            uCloudBandWidth: { value: cloudBandWidth },
            uCloudBandStrength: { value: cloudBandStrength },
        },
        side: THREE.DoubleSide,
        toneMapped: false,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    mesh.renderOrder = renderOrder;
    mesh.frustumCulled = false;
    return mesh;
}

function buildAlpineHorizon(sunDirection = getSunDirection(), { aspect = 1512 / 796 } = {}) {
    const group = new THREE.Group();
    group.name = 'alpine-horizon';
    group.userData.kind = 'procedural-mountain-ring';

    const sun = normalizedSunDirection(sunDirection);
    // Put the dominant horn in the initial viewing direction so it reads as the
    // hero peak (Matterhorn) front and centre.
    const forward = getInitialViewForward(sunDirection, aspect);
    const heroAngle = Math.atan2(forward.z, forward.x);

    // Two lit, snowy, jagged ranges — far towering giants (with the hero horn),
    // a nearer massif for depth. Dark rock on the steep faces, snow on the tops.
    const layers = [
        {
            name: 'alpine-range-far', renderOrder: 2,
            radius: MOUNTAIN_RING_DISTANCE, peaks: 15,
            minHeight: 90, maxHeight: MOUNTAIN_MAX_HEIGHT,
            radialSlope: 0.5, tangentOverlap: 0.62, sides: 9,
            snowLineY: 195, snowBand: 80, heroAngle, heroBoost: 0.85, heroWidth: 0.055, heroRadialSlope: 0.22,
            rockLight: COLOUR_ROCK_FAR_LIGHT, rockDark: COLOUR_ROCK_FAR_DARK, hazeStrength: 0.8, sunDir: sun, seedBase: 3.1,
        },
        {
            name: 'alpine-range-mid', renderOrder: 3,
            radius: 1120, peaks: 22,
            minHeight: 48, maxHeight: 200,
            radialSlope: 0.52, tangentOverlap: 0.6, sides: 9,
            snowLineY: 118, snowBand: 48, heroAngle, heroBoost: 0.0,
            rockLight: COLOUR_ROCK_MID_LIGHT, rockDark: COLOUR_ROCK_MID_DARK, hazeStrength: 0.5, sunDir: sun, seedBase: 9.4,
        },
    ];

    layers.forEach((layer) => group.add(buildAlpineRange(layer)));
    return group;
}

export function createFieldFog() {
    return new THREE.Fog(FIELD_FOG_COLOR, FIELD_FOG_NEAR, FIELD_FOG_FAR);
}

// ─── Public API ─────────────────────────────────────────────────────────

export function setFieldSunDirection(fieldScene, sunDirection) {
    const sun = normalizedSunDirection(sunDirection);
    if (!fieldScene) return sun;

    fieldScene.userData.sunDirection = sun.clone();

    const pitch = getPitchMesh(fieldScene);
    if (pitch?.material?.uniforms?.uSunDirection) {
        pitch.material.uniforms.uSunDirection.value.copy(sun);
    }

    const sky = fieldScene.getObjectByName('procedural-sky');
    if (sky?.material?.uniforms?.uSunDirection) {
        sky.material.uniforms.uSunDirection.value.copy(sun);
    }

    const rig = fieldScene.getObjectByName('sun-light-rig');
    if (rig) {
        rig.userData.sunDirection = sun.clone();
        const light = rig.getObjectByName('sun-directional-light');
        if (light) {
            light.position.copy(sun).multiplyScalar(280);
        }
        const flare = rig.getObjectByName('sun-flare');
        if (flare) {
            flare.position.copy(sun).multiplyScalar(SKY_DOME_RADIUS * 0.74);
            flare.userData.sunDirection = sun.clone();
        }
    }

    return sun;
}

/**
 * Apply a full celestial state to the field scene — primary light direction,
 * light colour, intensity, sky palette, and sun/moon disc positions. This
 * supersedes setFieldSunDirection for any caller that wants day/night
 * transitions; setFieldSunDirection still works for legacy code that only
 * cares about the sun vector.
 */
export function setFieldCelestialState(fieldScene, state) {
    if (!fieldScene || !state) return;

    fieldScene.userData.celestialState = state;
    fieldScene.userData.sunDirection = state.primaryDirection.clone();

    const pitch = getPitchMesh(fieldScene);
    if (pitch?.material?.uniforms) {
        const u = pitch.material.uniforms;
        u.uSunDirection.value.copy(state.primaryDirection);
        u.uSunColor.value.copy(state.primaryColor);
        u.uSunIntensity.value = state.primaryIntensity;
        u.uSkyAmbient.value.copy(state.ambientColor);
        u.uSkyIntensity.value = state.ambientIntensity;
    }

    const sky = fieldScene.getObjectByName('procedural-sky');
    if (sky?.material?.uniforms) {
        const u = sky.material.uniforms;
        // Sky uses the real sun + moon for disc rendering, and the primary
        // colour for tinting the glow around whichever body is up.
        u.uSunDirection.value.copy(state.sunDirection);
        if (u.uMoonDirection) u.uMoonDirection.value.copy(state.moonDirection);
        u.uSunColor.value.copy(state.primaryColor);
        u.uZenithColor.value.copy(state.skyZenith);
        u.uHorizonColor.value.copy(state.skyHorizon);
        u.uHazeColor.value.copy(state.skyHaze);
        u.uCloudOpacity.value = state.cloudOpacity;
        if (u.uNightFactor) u.uNightFactor.value = 1 - state.dayFactor;
    }

    // Dim the alpine backdrop with the day/night cycle so it recedes into a
    // dark silhouette at 3am instead of glaring against the night sky.
    const horizon = fieldScene.getObjectByName('alpine-horizon');
    if (horizon) {
        horizon.traverse((child) => {
            const u = child.material?.uniforms;
            if (!u) return;
            if (u.uDaylight) u.uDaylight.value = state.dayFactor;
            if (u.uSunDir) u.uSunDir.value.copy(state.primaryDirection);
            if (u.uHazeColor) u.uHazeColor.value.copy(state.skyHaze);
        });
    }

    const rig = fieldScene.getObjectByName('sun-light-rig');
    if (rig) {
        rig.userData.sunDirection = state.primaryDirection.clone();
        const light = rig.getObjectByName('sun-directional-light');
        if (light) {
            light.position.copy(state.primaryDirection).multiplyScalar(280);
            light.color.copy(state.primaryColor);
            light.intensity = state.primaryIntensity;
        }
        const flare = rig.getObjectByName('sun-flare');
        if (flare) {
            // Hide the lens flare entirely at night; the sun is below the
            // horizon and the moon doesn't share its halo geometry.
            flare.visible = state.sunElevationDeg > 0;
            if (flare.visible) {
                flare.position.copy(state.sunDirection).multiplyScalar(SKY_DOME_RADIUS * 0.74);
                flare.userData.sunDirection = state.sunDirection.clone();
            }
        }
    }
}

/**
 * Build the daylight field backdrop as a Group ready to add to a scene.
 * `group.userData.pitchMesh` is the mesh consumers should raycast against
 * to position plants — it's a flat plane at y=0.
 */
export function createFieldScene({ sunDirection = getSunDirection(), aspect = 1512 / 796 } = {}) {
    const sun = normalizedSunDirection(sunDirection);
    const group = new THREE.Group();
    group.name = 'field-scene';
    group.userData.sunDirection = sun.clone();

    group.add(buildDaylightSky(sun));
    group.add(buildAlpineHorizon(sun, { aspect }));
    group.add(buildSunLightRig(sun));
    const pitch = buildPitchMesh(sun);
    group.add(pitch);
    group.userData.pitchMesh = pitch;

    return group;
}

/**
 * The mesh consumers should raycast against to land plants on the pitch.
 */
export function getPitchMesh(scene) {
    return scene.userData?.pitchMesh ?? null;
}
